/**
 * iOS WebKit has two documented gaps this client works around visibly
 * rather than silently (BUILD_PLAN SC3 notes, architecture.md section 4.1):
 * no Background Sync API, so a reconnect upload only fires while the app
 * is open in the foreground; and no advanced `MediaTrackConstraints`
 * (torch, zoom, precise facingMode), so capture falls back to the browser
 * file-picker camera intent instead of a live constrained stream.
 *
 * Every caller of these checks is expected to render a `DegradedNotice`
 * (src/components/capture/DegradedNotice.tsx) when they return true. A
 * degradation the operator cannot see is the failure mode this module
 * exists to prevent.
 */

export function isIosWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIosDevice = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
  const isWebKit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIosDevice && isWebKit;
}

export function supportsBackgroundSync(): boolean {
  if (typeof window === 'undefined') return false;
  return 'serviceWorker' in navigator && 'SyncManager' in window;
}

export function supportsAdvancedCameraConstraints(): boolean {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return false;
  // iOS WebKit exposes getUserMedia but ignores torch/zoom/facingMode
  // constraints. There is no capability query for this gap, so it is
  // inferred from the platform check rather than a feature-detect.
  return !isIosWebKit();
}

export function supportsBarcodeDetector(): boolean {
  return typeof window !== 'undefined' && 'BarcodeDetector' in window;
}

/**
 * Whether the camera is this device's primary capture source.
 *
 * A coarse pointer is a finger, so the machine is the phone or tablet the
 * operator carries into the aisle, held up to the item already. A fine
 * pointer is a mouse, so the machine sits on a desk and the photograph
 * reached it as a file. Opening a desktop capture screen used to request
 * `getUserMedia` on mount regardless, which raised a permission prompt for a
 * webcam pointed at the reviewer rather than at any equipment.
 *
 * `matchMedia` is missing under jsdom, where the camera-first answer keeps
 * the existing test behaviour.
 */
export function prefersCameraCapture(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(pointer: coarse)').matches;
}

export interface PlatformDegradation {
  backgroundSync: boolean;
  cameraConstraints: boolean;
  barcodeDetector: boolean;
  /** One clause per missing capability, for a caller that renders a list. */
  gaps: string[];
  message: string | null;
}

/** One call site for every screen that needs to decide whether to render
 * a `DegradedNotice`. `message` is null when nothing is degraded. */
export function getPlatformDegradation(): PlatformDegradation {
  const backgroundSync = supportsBackgroundSync();
  const cameraConstraints = supportsAdvancedCameraConstraints();
  const barcodeDetector = supportsBarcodeDetector();

  // Each clause names the missing capability alone. The heading already says
  // "on this device", so repeating it inside a clause read as a stutter.
  const gaps: string[] = [];
  if (!backgroundSync) {
    gaps.push('Queued captures upload only while this app is open.');
  }
  if (!cameraConstraints) {
    gaps.push('Camera zoom, torch and lens switching are unavailable.');
  }
  if (!barcodeDetector) {
    gaps.push('Barcode capture falls back to manual entry.');
  }

  return {
    backgroundSync,
    cameraConstraints,
    barcodeDetector,
    gaps,
    message: gaps.length > 0 ? `Reduced capability on this device. ${gaps.join(' ')}` : null,
  };
}
